import { getSearchParamInsensitive } from "@/lib/attribution/extract-utm-from-url"
import { getEventPartnershipOffer } from "@/lib/event-partnerships/catalog"
import { PARTNER_SLUG_PARAM } from "@/lib/partners/constants"
import { getPartnerSlugFromPathname } from "@/lib/partners/partner-path"
import { normalizePartnerSlug } from "@/lib/partners/partner-slug-cookie"
import { REFERRAL_CODE_PARAM } from "@/lib/referral/constants"
import { normalizeReferralCode } from "@/lib/referral/referral-code-cookie"

/** `medium` em `attribution_touches` para tráfego MGM (complementa `referral_attributions`). */
export const PROGRAM_MEDIUM_MGM = "mgm"

/** `medium` em `attribution_touches` para tráfego de parceria (complementa `partner_attributions`). */
export const PROGRAM_MEDIUM_PARTNERSHIP = "parceria"

export const PROGRAM_CAMPAIGN_MGM = "indique-ganhe"

export type ProgramChannelMedium = typeof PROGRAM_MEDIUM_MGM | typeof PROGRAM_MEDIUM_PARTNERSHIP

export interface ProgramChannel {
  source: string
  medium: ProgramChannelMedium
  campaign: string | null
}

export interface UtmLikeFields {
  source?: string | null
  medium?: string | null
  campaign?: string | null
  gclid?: string | null
  fbclid?: string | null
  ttclid?: string | null
}

function getRawQueryValue(
  rawQuery: Record<string, string | string[]> | null | undefined,
  key: string,
): string | null {
  if (!rawQuery) return null
  const target = key.toLowerCase()
  for (const [k, value] of Object.entries(rawQuery)) {
    if (k.toLowerCase() !== target) continue
    if (Array.isArray(value)) {
      const first = value.find((v) => typeof v === "string" && v.trim())
      return first?.trim() ?? null
    }
    if (typeof value === "string" && value.trim()) return value.trim()
  }
  return null
}

/**
 * Resolve canal MGM ou parceria para `source`/`medium` de attribution.
 * Prioridade: LP `/parcerias/{slug}` > `?partner=` > `?ref=` > valores persistidos (cookie/LS).
 */
export function resolveProgramChannelFromVisit(input: {
  pageUrl: URL
  pathname?: string | null
  storedReferralCode?: string | null
  storedPartnerSlug?: string | null
}): ProgramChannel | null {
  const pathname = input.pathname ?? input.pageUrl.pathname

  const partnerFromPath = getPartnerSlugFromPathname(pathname)
  const partnerFromParam = normalizePartnerSlug(
    getSearchParamInsensitive(input.pageUrl.searchParams, PARTNER_SLUG_PARAM),
  )
  const partnerStored = normalizePartnerSlug(input.storedPartnerSlug ?? null)

  const partnerSlug = partnerFromPath ?? partnerFromParam ?? partnerStored
  if (partnerSlug) {
    return {
      source: partnerSlug,
      medium: PROGRAM_MEDIUM_PARTNERSHIP,
      campaign: getEventPartnershipOffer(partnerSlug)?.utmCampaign ?? null,
    }
  }

  const refFromUrl = normalizeReferralCode(
    getSearchParamInsensitive(input.pageUrl.searchParams, REFERRAL_CODE_PARAM),
  )
  const refStored = normalizeReferralCode(input.storedReferralCode ?? null)
  const refCode = refFromUrl ?? refStored
  if (refCode) {
    return {
      source: refCode.toLowerCase(),
      medium: PROGRAM_MEDIUM_MGM,
      campaign: PROGRAM_CAMPAIGN_MGM,
    }
  }

  return null
}

/** Deriva canal a partir de páginas já gravadas no touch (server / signup). */
export function resolveProgramChannelFromAttributionPages(input: {
  currentPage?: string | null
  landingPage?: string | null
  rawQuery?: Record<string, string | string[]> | null
}): ProgramChannel | null {
  for (const href of [input.currentPage, input.landingPage]) {
    if (!href?.trim()) continue
    try {
      const url = new URL(href)
      const channel = resolveProgramChannelFromVisit({ pageUrl: url, pathname: url.pathname })
      if (channel) return channel
    } catch {
      // URL inválida — ignora
    }
  }

  const partnerFromQuery = normalizePartnerSlug(getRawQueryValue(input.rawQuery, PARTNER_SLUG_PARAM))
  if (partnerFromQuery) {
    return {
      source: partnerFromQuery,
      medium: PROGRAM_MEDIUM_PARTNERSHIP,
      campaign: getEventPartnershipOffer(partnerFromQuery)?.utmCampaign ?? null,
    }
  }

  const refFromQuery = normalizeReferralCode(getRawQueryValue(input.rawQuery, REFERRAL_CODE_PARAM))
  if (refFromQuery) {
    return {
      source: refFromQuery.toLowerCase(),
      medium: PROGRAM_MEDIUM_MGM,
      campaign: PROGRAM_CAMPAIGN_MGM,
    }
  }

  return null
}

/** Não sobrescreve UTMs pagos nem click IDs. */
export function shouldApplyProgramChannelToUtm(fields: UtmLikeFields): boolean {
  if (fields.source?.trim() || fields.medium?.trim()) return false
  if (fields.gclid?.trim() || fields.fbclid?.trim() || fields.ttclid?.trim()) return false
  return true
}

export function mergeProgramChannelIntoUtmFields<T extends UtmLikeFields>(
  fields: T,
  channel: ProgramChannel | null,
): T {
  if (!channel || !shouldApplyProgramChannelToUtm(fields)) {
    return fields
  }

  return {
    ...fields,
    source: channel.source,
    medium: channel.medium,
    campaign: fields.campaign?.trim() ? fields.campaign : channel.campaign,
  }
}

export function isProgramChannelMedium(medium: string | null | undefined): boolean {
  const m = medium?.trim().toLowerCase()
  return m === PROGRAM_MEDIUM_MGM || m === PROGRAM_MEDIUM_PARTNERSHIP
}

export function isPartnerLandingUrl(url: string | null | undefined): boolean {
  if (!url?.trim()) return false
  try {
    return getPartnerSlugFromPathname(new URL(url).pathname) !== null
  } catch {
    return false
  }
}

export function hasReferralOrPartnerQuery(
  rawQuery: Record<string, string | string[]> | null | undefined,
): boolean {
  const ref = normalizeReferralCode(getRawQueryValue(rawQuery, REFERRAL_CODE_PARAM))
  const partner = normalizePartnerSlug(getRawQueryValue(rawQuery, PARTNER_SLUG_PARAM))
  return Boolean(ref || partner)
}
